import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useReturnDialog } from "@/features/resi/useReturnDialog";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { MACRO_CHIP_IDS, OrdiniFilterCard, PAYMENT_CHIP_IDS } from "./components/OrdiniFilterCard";
import { OrdiniRitiriSide } from "./components/OrdiniRitiriSide";
import { useProssimoRitiro } from "./useProssimoRitiro";
import { buildEmptyRitiri } from "./ritiriVuoti";
import { OrdiniActionToolbar } from "./components/OrdiniActionToolbar";
import { OrdiniListHeader } from "./components/OrdiniListHeader";
import { ListaTroncataAvviso } from "./components/ListaTroncataAvviso";
import { OrderCard, orderMarketplaceLogo } from "./components/OrderCard";
import { OrderCardMobile } from "./components/OrderCardMobile";
import {
  OrdiniFiltriFoglio,
  OrdiniRicercaMobile,
  contaFiltriFoglio,
} from "./components/OrdiniFiltriMobile";
import { useIsMobile } from "@/shared/useMediaQuery";
import { SyncDetailPanel } from "./components/SyncDetailPanel";
import { SyncStatusPill } from "./components/SyncStatusPill";
import { HaltedLanesAlert } from "./components/HaltedLanesAlert";
import { bulkChangeStatus, useOrders, useRitiriOggi } from "./api";
import { STATO_SPEDITO } from "./confermaBulk";
import { ConfermaSpedizioneBulk } from "./components/SpedizioneBulkConfirmDialog";
import { OrdiniBarraMobile } from "./components/OrdiniBarraMobile";
import { OrdiniColumnHeader } from "./components/OrdiniColumnHeader";
import { OrdiniFiltriSheet } from "./components/OrdiniFiltriSheet";
import { OrdiniVuoto } from "./components/OrdiniVuoto";
import { defaultDateRange } from "./dateRange";
import { raggruppaPerGiorno } from "./giorniLista";
import { bonificoSelezione } from "./bonificoSelezione";
import { useOrderMargins } from "./useOrderMargins";
import { usePickupMailAction } from "./usePickupMailAction";
import { usePortalOrderAction } from "./usePortalOrderAction";
import { apiPost, prettifyApiError } from "@/shared/api";
import { stringSetCodec, usePersistedState } from "@/shared/usePersistedState";
import { useDebounced } from "@/shared/useDebounced";
import { mockOrders } from "./data/mockOrders";
import { mockRitiri } from "./data/mockRitiri";
import { opzioniFornitore, ordineDelFornitore } from "./fornitoreFiltro";
import type { Order } from "./types";
import {
  PAYMENT_FILTER_SUPPLIERS,
  STATE_CHIP_OR_PREDICATES,
  STATE_CHIP_PREDICATES,
  STATE_CHIP_TO_PAYMENT,
  SUPPLIER_PAYMENT_CHIP_IDS,
  SUPPLIER_PAYMENT_STATES,
  passaChip,
} from "./chipFiltri";

// Le regole dei chip vivono in chipFiltri.ts; i test le leggono anche da qui.
export { STATE_CHIP_PREDICATES, STATE_CHIP_TO_PAYMENT, passaChip };
import type { SyncStep } from "./components/OrdiniActionToolbar";

// Endpoint "Processa Mail Fornitori": legge le CONFERME SPEDIZIONE da IMAP
// Hostinger (modulo supplier_mail: merce pronta, conferme AS/WF, scan Sent).
// Estratto in costante condivisa fra ``runSyncStep`` (step singolo) e
// ``runAllSyncs`` (bottone "Sincronizza") per evitare il drift che aveva
// causato il bug: ``runAllSyncs`` chiamava ``/api/mail/sync`` (sync MESSAGGI
// marketplace = MessaggiPage) invece di questo, quindi premendo "Sincronizza"
// le conferme spedizione fornitori NON venivano processate (audit 2026-06-24).
// NB: ``/api/mail/sync`` resta il sync dei thread marketplace, sul bottone
// dedicato "Sincronizza messaggi" in MessaggiPage — sono due cose diverse.
const SUPPLIER_MAIL_RUN_NOW_ENDPOINT = "/api/sync/intervals/supplier_mail/run-now";

/** I chip accesi al primo accesso e dopo «Reimposta». */
const CHIP_DI_PARTENZA = ["da_evadere"];

export function OrdiniPage() {
  const { search: locationSearch } = useLocation();
  const isMockMode = new URLSearchParams(locationSearch).get("mock") === "1";

  // Filtri/ricerca persistiti in sessionStorage (W2, item 9 Davide "cambiando
  // pagina si resetta tutto"): navigando a /ordini/:ref o /clienti e tornando,
  // la pagina rimonta ma search/date/fornitori/chip sopravvivono per la
  // sessione. Chiavi namespaced om.ordini.*. I default (da_evadere, range 30gg)
  // valgono solo al primo accesso della sessione; lo stato ripristinato parte
  // gia' nel primo render → useOrders lancia la query coi valori giusti.
  const [search, setSearch] = usePersistedState("om.ordini.search", "");
  const [defaultDates] = useState(() => defaultDateRange());
  // ponytail: chiavi date suffissate ``.iso`` perche' il formato persistito e'
  // cambiato (gg/mm/aaaa → ISO, <input type="date">). Chiave nuova = il vecchio
  // valore non viene mai letto e la scheda gia' aperta al deploy riparte dal
  // default invece di mandare "17/06/2026" all'API. Niente shim di conversione:
  // sessionStorage muore con la scheda, l'orfano si smaltisce da solo.
  const [dateFrom, setDateFrom] = usePersistedState("om.ordini.dateFrom.iso", defaultDates.from);
  const [dateTo, setDateTo] = usePersistedState("om.ordini.dateTo.iso", defaultDates.to);
  // Default: ordini "da evadere". Il fornitore e' ora un filtro a
  // discesa separato (default "Tutti": nessuna restrizione fornitore).
  const [activeChips, setActiveChips] = usePersistedState<Set<string>>(
    "om.ordini.chips",
    new Set(CHIP_DI_PARTENZA),
    stringSetCodec,
  );
  // Id fornitore (pagina Fornitori o catalogo PIM): vedi fornitoreFiltro.ts.
  const [supplierFilter, setSupplierFilter] = usePersistedState<Set<string>>(
    "om.ordini.suppliers",
    new Set(),
    stringSetCodec,
  );
  // Filtro canale di vendita (item 5): stesso pattern del filtro fornitore, ma
  // le opzioni sono DINAMICHE dagli ordini caricati (nessuna lista statica da
  // mantenere). Match su orderMarketplaceLogo(o).label (fonte unica del canale).
  const [channelFilter, setChannelFilter] = usePersistedState<Set<string>>(
    "om.ordini.channels",
    new Set(),
    stringSetCodec,
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [toolbarBusyStep, setToolbarBusyStep] = useState<SyncStep | "all" | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  // Spedizione in lotto: le reference in attesa di conferma nel dialog.
  const [spedizioneBulk, setSpedizioneBulk] = useState<string[] | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Banner sync espandibile: chiuso di default per non ingombrare la
  // pagina con dettagli ogni volta. Davide lo apre quando vuole capire
  // se i job stanno girando davvero (richiesta esplicita: "modo per
  // capire che effettivamente la sync stia andando").
  const [syncDetailOpen, setSyncDetailOpen] = useState(false);
  // Telefono (redesign 2026-10-23): filtri e azioni vivono in due fogli dal
  // basso invece che nella plancia, che a 412px occupava tutto il primo schermo.
  const isMobile = useIsMobile();
  const [filtriAperti, setFiltriAperti] = useState(false);
  const [azioniAperte, setAzioniAperte] = useState(false);
  // Ricerca debounced come ClientiPage/FatturazionePage/MessaggiPage: la GET
  // /api/orders?limit=500 parte dopo 300 ms di quiete invece che a ogni tasto
  // premuto. L'input resta controllato da ``search``, quindi la digitazione non
  // rallenta: è solo la query a essere ritardata.
  const debouncedSearch = useDebounced(search, 300);
  const liveOrders = useOrders({
    enabled: !isMockMode,
    search: debouncedSearch,
    dateFrom,
    dateTo,
  });
  const allOrders = isMockMode ? mockOrders : liveOrders.orders;
  // Opzioni canale dinamiche (item 5): label univoche dagli ordini caricati,
  // ordinate. value=label perche' il match nel filtro e' sulla label del canale.
  // Opzioni fornitore: anagrafica + chiunque compaia nel catalogo degli ordini
  // caricati (fornitoreFiltro.ts). Aggiungere un fornitore al PIM lo fa
  // comparire qui da solo — prima serviva una riga in suppliers.ts.
  const supplierOptions = useMemo(
    () => opzioniFornitore(allOrders, supplierFilter),
    [allOrders, supplierFilter],
  );
  const channelOptions = useMemo(
    () =>
      [...new Set(allOrders.map((o) => orderMarketplaceLogo(o).label))]
        .sort((a, b) => a.localeCompare(b, "it"))
        .map((label) => ({ value: label, label })),
    [allOrders],
  );
  // Fetch live del riepilogo "Ritiri di oggi" da /api/orders/pickup-summary
  // (polling 60s). In mock mode usiamo il fixture statico per design preview.
  const liveRitiri = useRitiriOggi(!isMockMode);
  const prossimoRitiro = useProssimoRitiro(isMockMode);
  // Audit 2026-05-13: useMemo cosi' ``emptyRitiri`` cambia solo se i
  // valori a monte cambiano. ``buildEmptyRitiri()`` valuta la data al
  // momento del render (non al boot del modulo).
  const ritiri = isMockMode ? mockRitiri : (liveRitiri ?? buildEmptyRitiri());

  // Applichiamo i filtri lato client. Gruppi in AND fra loro, OR
  // all'interno. Chip "stato" → `paymentState`; il fornitore e' ora un
  // filtro a discesa indipendente (single-select) → `fornitore.kind`.
  const orders = useMemo(() => {
    const stateChips = [...activeChips].filter((id) => id in STATE_CHIP_TO_PAYMENT);
    // Un chip pagamento-fornitore attivo restringe il risultato a Itamoby/Ityhome.
    const supplierPaymentChipActive = stateChips.some((id) =>
      SUPPLIER_PAYMENT_CHIP_IDS.includes(id),
    );
    // Chip con predicato custom (es. pronta_spedizione → pickupReady).
    // Lavorano in OR fra loro e con i chip paymentState.
    const predicateChips: Array<(o: Order) => boolean> = [];
    for (const id of activeChips) {
      const pred = STATE_CHIP_PREDICATES[id];
      if (pred) predicateChips.push(pred);
    }
    const supplierActive = supplierFilter.size > 0;
    const channelActive = channelFilter.size > 0;
    if (
      stateChips.length === 0 &&
      predicateChips.length === 0 &&
      !supplierActive &&
      !channelActive
    ) {
      return allOrders;
    }
    // Quando l'utente attiva SOLO chip "predicato" (es. pronta_spedizione)
    // senza alcun chip paymentState, non vogliamo filtrare via paymentState
    // (allowedPayments = null) altrimenti svuoteremmo la lista.
    // Audit 2026-05-19 (Davide "non vedo gli ordini pronti"): un chip
    // mappato a [] in STATE_CHIP_TO_PAYMENT (es. ``pronta_spedizione``)
    // NON deve restringere i pagamenti a un Set VUOTO.
    // Prima: selezionando SOLO "Spedizione Pronta", ``stateChips`` =
    // ["pronta_spedizione"] → ``new Set([])`` (vuoto ma truthy) →
    // ``!allowedPayments.has(o.paymentState)`` era true per OGNI ordine
    // → lista SEMPRE vuota, PRIMA ancora che il predicato pickup potesse
    // agire. Ora: se la lista pagamenti risultante e' vuota → nessuna
    // restrizione di pagamento (decidono i soli predicati, es. pickup).
    // Faceted (item 2, Davide): OR DENTRO la riga di chip, AND FRA le righe.
    // Prima TUTTI i chip finivano in un UNICO gruppo OR, quindi "Da evadere" +
    // "Non ordinato" ALLARGAVA invece di restringere — l'utente si aspetta
    // l'intersezione (da evadere E non ancora ordinato al fornitore).
    // Un chip mappato a [] senza OR-predicato (pronta_spedizione, gestito in
    // AND da STATE_CHIP_PREDICATES) ritorna null = nessun criterio, cosi' non
    // svuota la lista (bug "Set vuoto" 2026-05-19).
    const chipGroupMatcher = (ids: string[]): ((o: Order) => boolean) | null => {
      const active = ids.filter((id) => activeChips.has(id));
      if (active.length === 0) return null;
      const payments = active.flatMap((id) => STATE_CHIP_TO_PAYMENT[id] ?? []);
      const allowed = payments.length ? new Set(payments) : null;
      const preds = active
        .map((id) => STATE_CHIP_OR_PREDICATES[id])
        .filter((p): p is (o: Order) => boolean => Boolean(p));
      if (allowed === null && preds.length === 0) return null;
      return (o: Order) =>
        (allowed ? allowed.has(o.paymentState) : false) || preds.some((p) => p(o));
    };
    const macroMatcher = chipGroupMatcher(MACRO_CHIP_IDS);
    const paymentMatcher = chipGroupMatcher(PAYMENT_CHIP_IDS);
    // Fornitore: filtro multi-select indipendente. Set vuoto = nessuna
    // restrizione; altrimenti l'ordine passa se il suo kind e' fra quelli
    // spuntati (OR all'interno del gruppo fornitore).
    return allOrders.filter((o: Order) => {
      // Chip stato/pagamento: OR all'interno del gruppo (paymentState ammesso
      // OPPURE un predicato OR misto matcha). Fornitore: AND (restrizione
      // singola dalla tendina). Predicato pronta_spedizione: AND con i macro.
      //
      // Audit 2026-05-12 fix #2 (Davide): prima predicate matchato faceva
      // return true saltando i filtri macro → ``001-26117L65751-A`` con
      // pickupReady=false passava perche' da_evadere+ityhome lo includevano,
      // ma chip "pronta_spedizione" non lo limitava. Adesso predicate e'
      // AND: pickupReady DEVE essere true se il chip e' attivo, oltre a
      // soddisfare gli altri filtri.
      // AND fra le due righe di chip (item 2): ciascuna riga restringe per conto
      // suo, l'OR resta solo FRA i chip della stessa riga.
      if (macroMatcher && !macroMatcher(o)) return false;
      if (paymentMatcher && !paymentMatcher(o)) return false;
      // Gate pagamento-fornitore: "Pagato"/"Da pagare" mostrano SOLO Itamoby/
      // Ityhome (richiesta Davide 2026-06-08, coerente coi badge pagamento).
      // Eccezione item 13: gli ordini con supplierUnpaid restano sempre
      // visibili (sono comunque Ita/Ity, ma la guardia e' esplicita).
      if (
        supplierPaymentChipActive &&
        SUPPLIER_PAYMENT_STATES.has(o.paymentState) &&
        !PAYMENT_FILTER_SUPPLIERS.has(o.fornitore.kind) &&
        o.supplierUnpaid !== true
      ) {
        return false;
      }
      if (supplierActive) {
        // OR fra i fornitori spuntati.
        const matches = [...supplierFilter].some((id) => ordineDelFornitore(o, id));
        if (!matches) return false;
      }
      // Canale di vendita (item 5): AND col resto, OR fra i canali spuntati.
      if (channelActive && !channelFilter.has(orderMarketplaceLogo(o).label)) {
        return false;
      }
      for (const pred of predicateChips) {
        if (!pred(o)) return false;
      }
      return true;
    });
  }, [allOrders, activeChips, supplierFilter, channelFilter]);
  // Numeri sui chip: quanti ordini del periodo e della ricerca entrano in ogni
  // coda, rispettando fornitore e canale scelti nelle tendine. Non dipendono
  // dagli altri chip accesi: dicono cosa trovi cliccando, non cosa vedi ora.
  const conteggiChip = useMemo(() => {
    const base = allOrders.filter(
      (o) =>
        (supplierFilter.size === 0 ||
          [...supplierFilter].some((id) => ordineDelFornitore(o, id))) &&
        (channelFilter.size === 0 || channelFilter.has(orderMarketplaceLogo(o).label)),
    );
    const conteggi: Record<string, number> = {};
    for (const id of [...MACRO_CHIP_IDS, ...PAYMENT_CHIP_IDS]) {
      conteggi[id] = base.filter((o) => passaChip(o, id)).length;
    }
    return conteggi;
  }, [allOrders, supplierFilter, channelFilter]);
  // La lista a blocchi di giorni (redesign 2026-10-27): «Oggi», «Ieri»,
  // «sabato 25 ottobre». L'ordine resta quello del backend.
  const giorni = useMemo(() => raggruppaPerGiorno(orders, (o) => o.createdAtPoleepo), [orders]);
  const selectedOrders = useMemo(
    () => orders.filter((order) => selectedIds.has(order.id)),
    [orders, selectedIds],
  );
  // Quanto bonificare per la selezione corrente: il costo fornitore è netto,
  // il bonifico si fa sul lordo (Davide 2026-08-10).
  const bonifico = useMemo(() => bonificoSelezione(selectedOrders), [selectedOrders]);

  // Margine netto per-card dai parametri AM (Davide 2026-07-03): fetch batch
  // best-effort delle reference visibili, cache accumulata nell'hook.
  const orderRefs = useMemo(() => orders.map((order) => order.id), [orders]);
  // paramsSyncedAt non serve piu' qui: era passato a OrderCard solo per la
  // sezione Margine (AM) dell'esplosione (rimossa, Piano 3 Task 3) — la
  // pagina ordine /ordini/:ref lo mostra gia' via ContiSection.
  const { rows: marginByRef } = useOrderMargins(orderRefs, !isMockMode);

  // Audit 2026-05-13 #18: ``liveOrdersRef`` evita che ogni rerender del
  // polling 60s ricrei i callback ``handleDrainComplete`` /
  // ``handleGenerateLabel``. Senza, ad ogni tick le ``OrderCard`` figlie
  // vedevano nuove props funzione → re-render cascata su ~50 card.
  // Pattern: aggiornamento sincrono del ref durante render (no useEffect),
  // tipico per stabilizzare closure su valori che cambiano spesso ma di
  // cui ci serve solo l'ultimo snapshot.
  const liveOrdersRef = useRef(liveOrders);
  liveOrdersRef.current = liveOrders;

  // Quando il pill rileva fine drain (running+queued tornano a 0 dopo aver
  // visto attivita') rinfreschiamo la lista ordini cosi' Davide vede subito
  // i record arrivati senza fare F5 manuale.
  const handleDrainComplete = useCallback(() => {
    if (!isMockMode) void liveOrdersRef.current.refresh();
  }, [isMockMode]);

  // Audit 2026-05-11 L19: ``masterState`` deve confrontare i ``selectedOrders``
  // VISIBILI (post-filter) con la lista corrente ``orders``, non i
  // ``selectedIds`` globali. Senza questo fix, selezionare 3 ordini poi
  // toggle di un chip filtro che ne nasconde 2 lasciava il master in
  // "indeterminate" anche quando "tutti i visibili" erano selezionati.
  const masterState =
    selectedOrders.length === 0
      ? ("unchecked" as const)
      : selectedOrders.length === orders.length
        ? ("checked" as const)
        : ("indeterminate" as const);

  function handleChipToggle(id: string) {
    setActiveChips((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleToggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleMasterChange(checked: boolean) {
    setSelectedIds(checked ? new Set(orders.map((o) => o.id)) : new Set());
  }

  function handleClearSearch() {
    setSearch("");
  }

  function handleShowAll() {
    setActiveChips(new Set());
    setSupplierFilter(new Set());
    setChannelFilter(new Set());
    setSearch("");
  }

  // «Reimposta» (Davide 2026-09-23): il punto di partenza della pagina, cioe'
  // «Da evadere» negli ultimi 30 giorni. «Mostra tutti» invece spegne tutto.
  function handleReset() {
    handleShowAll();
    setActiveChips(new Set(CHIP_DI_PARTENZA));
    const partenza = defaultDateRange();
    setDateFrom(partenza.from);
    setDateTo(partenza.to);
  }

  async function runSyncStep(step: SyncStep) {
    if (isMockMode) return undefined;
    setToolbarBusyStep(step);
    setActionMessage(null);
    setActionError(null);
    try {
      if (step === "orders") {
        await liveOrders.syncNow({ dateFrom, dateTo });
        setActionMessage("Sync ordini avviato.");
      } else {
        // Audit 2026-05-28 (Davide): lo step "mail" del split-menu della
        // pagina Ordini si chiama "Processa Mail Fornitori" e deve
        // leggere le CONFERME SPEDIZIONE da IMAP Hostinger (modulo
        // supplier_mail), NON sincronizzare i thread marketplace
        // (modulo mail = MessaggiPage). Prima puntava per errore a
        // ``/api/mail/sync`` causando il bug "clicco Processa Mail e mi
        // sincronizza i messaggi". Il sync dei messaggi marketplace
        // resta sul bottone dedicato in MessaggiPage.
        await apiPost<unknown>(SUPPLIER_MAIL_RUN_NOW_ENDPOINT);
        setActionMessage("Processa Mail Fornitori avviato.");
      }
    } catch (err) {
      setActionError(prettifyApiError(err));
    } finally {
      setToolbarBusyStep(null);
    }
  }

  async function runAllSyncs() {
    if (isMockMode) return undefined;
    setToolbarBusyStep("all");
    setActionMessage(null);
    setActionError(null);
    try {
      await liveOrders.syncNow({ dateFrom, dateTo });
      // FIX 2 (audit 2026-06-24): qui andava ``/api/mail/sync`` (sync MESSAGGI
      // marketplace), per cui il bottone "Sincronizza" NON processava le
      // conferme spedizione fornitori. Ora usa lo stesso endpoint dello step
      // singolo "Processa Mail Fornitori" (supplier_mail), via costante
      // condivisa per evitare drift futuro.
      await apiPost<unknown>(SUPPLIER_MAIL_RUN_NOW_ENDPOINT);
      setActionMessage("Sync ordini e mail fornitori avviati.");
    } catch (err) {
      setActionError(prettifyApiError(err));
    } finally {
      setToolbarBusyStep(null);
    }
  }

  // Dialog reso/sostituzione: gestisce il prefill + submit via useReturnDialog.
  // `busyOrder` traccia quale ordine ha la richiesta prefill in corso (stringa
  // reference) → passiamo returnBusy={busyOrder === order.reference} a ogni card.
  // `dialog` è il ReactElement del ReturnDialog (null quando chiuso).
  const {
    openFor,
    dialog: returnDialog,
    busyOrder: returnBusyOrder,
    // Errore del prefill reso/sostituzione: senza renderlo, un prefill fallito
    // (backend giu', ordine assente) lascerebbe il click su "Reso"/"Sostituzione"
    // muto (nessun dialog montato). Regola: ogni pulsante dà feedback.
    openError: returnOpenError,
  } = useReturnDialog(() => {
    if (!isMockMode) void liveOrdersRef.current.refresh();
  });

  // «Invia LDV»: la macchina a stati (gate mail_backend, popup di conferma,
  // busy/keepBusy, refresh) vive in ``usePickupMailAction`` — la stessa che
  // usa la pagina ordine. ``liveOrdersRef`` è un ref: il callback resta
  // stabile e non ricrea le props delle ~50 OrderCard ad ogni tick del
  // polling (Audit 2026-05-13 #18).
  const refreshLiveOrders = useCallback(() => void liveOrdersRef.current.refresh(), []);
  // «Ordina a Ityhome» dalla singola card: STESSO hook della barra azioni, che
  // ne ha una propria istanza per il batch. Il popup di conferma è l'unica
  // strada verso l'ordine vero in entrambi i casi, e il backend tiene un lock
  // per fornitore — il carrello sul portale è uno solo per account.
  const portalItyhome = usePortalOrderAction({
    supplierId: "ityhome",
    supplierLabel: "ITYHOME",
    onMessage: setActionMessage,
    onError: setActionError,
    onRefresh: refreshLiveOrders,
  });
  const portalItamoby = usePortalOrderAction({
    supplierId: "itamoby",
    supplierLabel: "ITAMOBY",
    onMessage: setActionMessage,
    onError: setActionError,
    onRefresh: refreshLiveOrders,
  });
  const portalBizzotto = usePortalOrderAction({
    supplierId: "bizzotto",
    supplierLabel: "BIZZOTTO",
    onMessage: setActionMessage,
    onError: setActionError,
    onRefresh: refreshLiveOrders,
  });
  // Il tasto della card ordina sul portale del SUO fornitore.
  const portalBusy =
    portalItyhome.busy ||
    portalItyhome.dialogBusy ||
    portalItamoby.busy ||
    portalItamoby.dialogBusy ||
    portalBizzotto.busy ||
    portalBizzotto.dialogBusy;
  const pickupMail = usePickupMailAction({
    onRefresh: refreshLiveOrders,
    onMessage: setActionMessage,
    onError: setActionError,
    isMockMode,
  });

  // La barra azioni e' UNA: sul computer sta nella plancia, sul telefono nel
  // foglio «Azioni». Stesso componente, stessi gate, stesse conferme.
  const toolbar = (
    <OrdiniActionToolbar
      disabled={false}
      busyStep={toolbarBusyStep}
      onSyncAll={() => void runAllSyncs()}
      onSyncStep={(step) => void runSyncStep(step)}
      selectedOrders={selectedOrders}
      onAllServiceMessage={(message) => {
        setActionError(null);
        setActionMessage(message || null);
      }}
      onAllServiceError={(message) => {
        setActionMessage(null);
        setActionError(message || null);
      }}
      onAfterAllServiceAction={() => {
        if (!isMockMode) void liveOrders.refresh();
      }}
      onBulkChangeStatus={async (refs, newStatus) => {
        // #11 legacy parity: bulk change-status Poleepo per N ordini.
        // Dopo l'update mostriamo summary in actionMessage e
        // refreshiamo la lista cosi' Davide vede lo stato nuovo.
        setActionMessage(null);
        setActionError(null);
        // ⚠️ «Spedito» non è più un cambio di stato su Poleepo: è il
        // comando con cui si spedisce, e parla ai marketplace. Manda la
        // mail di spedizione a N clienti veri, quindi passa dal dialog.
        if (newStatus === STATO_SPEDITO) {
          setSpedizioneBulk(refs);
          return;
        }
        try {
          const result = await bulkChangeStatus(refs, newStatus);
          const msg =
            result.errors.length === 0
              ? `Stato aggiornato per ${result.updated} ordini.`
              : `Aggiornati ${result.updated} ordini, ${result.errors.length} errori (${result.errors[0]?.reference}: ${result.errors[0]?.error}).`;
          setActionMessage(msg);
          if (!isMockMode) void liveOrders.refresh();
        } catch (err) {
          setActionError(prettifyApiError(err));
        }
      }}
    />
  );

  const rigaStatoETestata = (
    <>
      {/* Riga di stato: pill live + eventuali messaggi azione/errore.
      Sempre presente cosi' Davide sa cosa sta facendo il backend
      anche prima di cliccare Sincronizza. Sta nello sticky con il resto:
      fuori, stando in mezzo, spezzerebbe il blocco fisso in due. */}
      {!isMockMode && (
        <div className="my-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <SyncStatusPill
              onDrainComplete={handleDrainComplete}
              onClick={() => setSyncDetailOpen((v) => !v)}
              expanded={syncDetailOpen}
            />
            {/* Audit 2026-05-11 C5: collassiamo la coppia ``actionError`` +
            ``liveOrders.error`` in un singolo pill. Quando il backend
            e' giu' le due variabili contenevano stringhe identiche
            ("Failed to fetch") e venivano renderizzate affiancate. La
            priorita' e' ``actionError`` perche' e' piu' specifica per
            l'azione appena triggerata; ``liveOrders.error`` resta come
            fallback per fallimenti del refresh in background. */}
            {/* Audit 2026-05-19: pannello con wrap multi-linea + ``break-words``
            cosi' i messaggi lunghi del backend Poleepo (es. errore corriere
            su peso/dimensioni) NON vengono troncati visivamente. ``max-w``
            generoso per dare spazio al dettaglio del corriere; resta
            compatto su messaggi brevi (cresce solo quando serve). */}
            <ErrorBanner
              message={actionError ?? returnOpenError ?? liveOrders.error}
              className="inline-flex items-start leading-relaxed max-w-[720px] whitespace-pre-wrap break-words"
            />
            {liveOrders.info && (
              <div className="inline-flex items-center px-3 py-1.5 rounded-full border border-blue/30 bg-blue-soft text-[11.5px] text-ink">
                {liveOrders.info}
              </div>
            )}
            <ListaTroncataAvviso caricati={liveOrders.orders.length} totale={liveOrders.total} />
            {actionMessage && (
              <div className="inline-flex items-center px-3 py-1.5 rounded-full border border-green/20 bg-green-soft text-[11.5px] text-ink">
                {actionMessage}
              </div>
            )}
          </div>
          {/* Avviso lane zombie: il circuit breaker del supervisor puo'
          fermare una lane (5 crash in 60s) in silenzio — la pill resta
          verde perche' guarda solo i job. Questo banner la rende visibile
          e suggerisce il riavvio. Vuoto in condizioni normali. */}
          <HaltedLanesAlert />
          <SyncDetailPanel open={syncDetailOpen} />
        </div>
      )}

      {/* Header lista mostrato solo quando ci sono ordini: con 0 ordini
      serve solo a creare rumore visivo (0 di 0 selezionati...). */}
      {orders.length > 0 && isMobile && (
        <div className="flex items-center justify-between pb-2 pt-1">
          <button
            type="button"
            onClick={() => handleMasterChange(masterState !== "checked")}
            aria-pressed={masterState === "checked"}
            className="om-press -ml-1 inline-flex h-10 items-center gap-2 rounded-full px-1 text-[14px] font-semibold text-ink"
          >
            <span
              aria-hidden="true"
              className={[
                "grid h-[22px] w-[22px] place-items-center rounded-md border-2",
                masterState === "unchecked" ? "border-line-2 bg-card" : "border-accent bg-accent",
              ].join(" ")}
            >
              {masterState === "indeterminate" ? (
                <span className="block h-0.5 w-2.5 rounded bg-card" />
              ) : (
                masterState === "checked" && (
                  <svg
                    className="h-3.5 w-3.5 fill-none stroke-white stroke-[3] [stroke-linecap:round] [stroke-linejoin:round]"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )
              )}
            </span>
            Seleziona tutti
          </button>
          <span className="text-[14px] tabular-nums text-ink" aria-live="polite">
            <strong className="font-bold">{orders.length}</strong> di {allOrders.length}
          </span>
        </div>
      )}
      {orders.length > 0 && !isMobile && (
        <OrdiniListHeader
          totalVisible={orders.length}
          // FIX 2 (audit 2026-06-24): ``totalAll`` deve essere il totale NON
          // filtrato (``allOrders``), non ``orders`` (gia' filtrato), altrimenti
          // "N visibili su N" anche con un filtro attivo (es. chip default
          // "da_evadere"). ``allOrders`` = mock o lista live pre-filtro.
          totalAll={allOrders.length}
          // FIX 3 (audit 2026-06-24): contatore "selezionati" = selezionati
          // VISIBILI (``selectedOrders``), non ``selectedIds.size`` (Set globale
          // che trattiene id nascosti da cambio chip/ricerca). Coerente con
          // master checkbox e azioni bulk, che usano gia' i visibili.
          selectedCount={selectedOrders.length}
          masterState={masterState}
          onMasterChange={handleMasterChange}
          bonifico={bonifico}
        />
      )}

      {/* Etichette colonna (Piano 3 Task 4): stessa griglia della card,
      appena sopra il map così resta allineata riga-su-riga.
      «Lista ordini» ed etichette stanno nello STICKY (Davide 2026-09-16):
      scorrendo, senza intestazioni non si sa piu' quale colonna e' quale.
      Il wrapper sticky ha gli stessi -mx/px del <main>, quindi la griglia
      resta larga quanto le card sotto. */}
    </>
  );

  const filtriFoglio = contaFiltriFoglio(
    activeChips,
    supplierFilter,
    channelFilter,
    dateFrom,
    dateTo,
  );

  return (
    // pt del main spostato nel wrapper sticky dei filtri: un margine/padding
    // sopra lo sticky lo farebbe attaccare a 12px dal bordo lasciando una
    // striscia in cui le card scorrono visibili.
    <main className="flex-1 overflow-y-auto px-7 pb-8 scroll-paper max-md:px-3">
      {/* Header filtri STICKY (Davide 2026-07-06): scrollando la lista i
          filtri restano raggiungibili. Margini negativi = copre il padding del
          <main> cosi' le card non sbucano ai lati/sopra; bg-paper = sfondo
          pagina. Si fissa TUTTO l'header come blocco unico (2026-07-27): due
          sticky impilati vorrebbero un offset misurato a runtime. */}
      <div
        data-testid="ordini-filters-sticky"
        className="sticky top-0 z-40 bg-paper -mx-7 px-7 pt-3 max-md:-mx-3 max-md:px-3"
      >
        {/* PLANCIA unica (Davide 2026-09-23: «incorpora tutte e 3 le schede in
            una sola, [...] senza occupare troppo spazio»): filtri e tasti
            azione a sinistra, ritiri a destra. Erano tre cornici e 271px di
            testata. `minmax(0,1fr)`: il minimo implicito di `1fr` e' il
            contenuto, e la riga sfondava di 24px a 1280. La colonna ritiri e'
            `auto`: larga coi ritiri, stretta nei giorni senza. ⚠️ Niente
            overflow-hidden sulla plancia: le tendine devono poterne uscire. */}
        {isMobile ? (
          <OrdiniRicercaMobile
            search={search}
            onSearchChange={setSearch}
            activeChips={activeChips}
            onChipToggle={handleChipToggle}
            filtriAttivi={filtriFoglio}
            conteggi={conteggiChip}
            onApriFiltri={() => setFiltriAperti(true)}
          />
        ) : (
          <section
            data-testid="ordini-plancia"
            aria-label="Filtri, azioni e ritiri"
            className="grid rounded-2xl border border-line bg-card shadow-[0_1px_3px_rgba(31,27,22,0.05)] md:[grid-template-columns:minmax(0,1fr)_auto]"
          >
            <div className="flex min-w-0 flex-col gap-2.5 px-4 py-3">
              <OrdiniFilterCard
                search={search}
                onSearchChange={setSearch}
                dateFrom={dateFrom}
                onDateFromChange={setDateFrom}
                dateTo={dateTo}
                onDateToChange={setDateTo}
                activeChips={activeChips}
                onChipToggle={handleChipToggle}
                onClearSearch={handleClearSearch}
                onShowAll={handleShowAll}
                onReset={handleReset}
                supplierOptions={supplierOptions}
                selectedSuppliers={supplierFilter}
                onSupplierFilterChange={setSupplierFilter}
                channelOptions={channelOptions}
                selectedChannels={channelFilter}
                onChannelFilterChange={setChannelFilter}
                conteggi={conteggiChip}
              />
              {/* Il filo separa cosa si VEDE (sopra) da cosa si FA (sotto). */}
              <div className="-mx-4 border-t border-line" />
              {toolbar}
            </div>
            <div className="rounded-r-2xl border-l border-line bg-paper max-md:hidden">
              <OrdiniRitiriSide ritiri={ritiri} prossimo={prossimoRitiro} />
            </div>
          </section>
        )}

        {!isMobile && rigaStatoETestata}
        {orders.length > 0 && !isMobile && <OrdiniColumnHeader />}
      </div>
      {/* Telefono: fissa resta solo la ricerca coi chip; stato del sync e
          «Seleziona tutti» scorrono via con la lista, o lo sticky si mangia
          un terzo dello schermo. */}
      {isMobile && rigaStatoETestata}

      <div aria-label="Lista ordini" role="region">
        {giorni.map((giorno) => (
          <section key={giorno.chiave} aria-label={giorno.titolo} className="mb-4 last:mb-0">
            <h3 className="flex items-baseline gap-2 px-1 pb-2 pt-1 font-display text-ink max-md:px-0.5">
              <span className="text-[15px] font-semibold tracking-[-0.01em] first-letter:uppercase max-md:text-[17px]">
                {giorno.titolo}
              </span>
              {giorno.sottotitolo && (
                <span className="text-[13px] font-medium max-md:text-[14px]">
                  {giorno.sottotitolo}
                </span>
              )}
              <span className="ml-auto font-sans text-[12.5px] tabular-nums max-md:text-[14px]">
                {giorno.ordini.length} {giorno.ordini.length === 1 ? "ordine" : "ordini"}
              </span>
            </h3>
            {giorno.ordini.map((order) => {
              const Card_ = isMobile ? OrderCardMobile : OrderCard;
              return (
                <Card_
                  key={order.id}
                  order={order}
                  isSelected={selectedIds.has(order.id)}
                  marginRow={marginByRef.get(order.id)}
                  pickupMailBusy={pickupMail.pickupMailBusyIds.has(order.id)}
                  onToggleSelect={handleToggleSelect}
                  onSendPickupMail={isMockMode ? undefined : pickupMail.sendPickupMail}
                  onOrderToSupplier={
                    isMockMode
                      ? undefined
                      : (o) => {
                          // Una mappa, non un ternario: col ternario un fornitore
                          // nuovo (Bizzotto) sarebbe finito in silenzio sul portale
                          // di Ityhome, cioè un ordine vero al fornitore sbagliato.
                          const azioni = {
                            ityhome: portalItyhome,
                            itamoby: portalItamoby,
                            bizzotto: portalBizzotto,
                          } as const;
                          const azione = azioni[o.portaleFornitore as keyof typeof azioni];
                          if (azione) void azione.start([o.id]);
                        }
                  }
                  orderToSupplierBusy={portalBusy}
                  onShippingSaved={
                    isMockMode ? undefined : () => void liveOrdersRef.current.refresh()
                  }
                  onCreateReturn={isMockMode ? undefined : (ref, kind) => void openFor(ref, kind)}
                  returnBusy={returnBusyOrder === order.id}
                />
              );
            })}
          </section>
        ))}
        {!isMockMode && orders.length === 0 && (
          <OrdiniVuoto
            caricamento={liveOrders.isLoading}
            inArchivio={allOrders.length > 0 ? liveOrders.total : 0}
            onMostraTutti={handleShowAll}
            syncBusy={liveOrders.isSyncing}
            onSync={() => {
              // ``syncNow`` non setta ``setError`` di proposito (delega al
              // caller, audit C4) e rilancia: ogni pulsante da' feedback.
              setActionError(null);
              liveOrders
                .syncNow({ dateFrom, dateTo })
                .catch((err) => setActionError(prettifyApiError(err)));
            }}
          />
        )}
      </div>

      {/* Dialog reso/sostituzione: montato qui (livello pagina) cosi' il
          dialog sopravvive al re-render della lista e mantiene la prop
          prefill intatta mentre l'utente compila il form. */}
      {spedizioneBulk && (
        <ConfermaSpedizioneBulk
          ids={spedizioneBulk}
          orders={orders}
          onFine={(esito) => {
            if (esito.tono === "ok") setActionMessage(esito.testo);
            else setActionError(esito.testo);
            setSpedizioneBulk(null);
            if (!isMockMode) void liveOrders.refresh();
          }}
          onAnnulla={() => setSpedizioneBulk(null)}
        />
      )}
      {isMobile && (
        <>
          <OrdiniBarraMobile
            syncBusy={toolbarBusyStep !== null}
            onSync={() => void runAllSyncs()}
            selezionati={selectedOrders.length}
            bonifico={bonifico}
            onAnnulla={() => setSelectedIds(new Set())}
            onAzioni={() => setAzioniAperte(true)}
            azioniAperte={azioniAperte}
            onChiudiAzioni={() => setAzioniAperte(false)}
          >
            {toolbar}
            {/* Esiti e errori anche qui: col foglio aperto la riga di stato
                della pagina e' coperta. */}
            <div className="mt-3 flex flex-col gap-2">
              <ErrorBanner message={actionError} className="whitespace-pre-wrap break-words" />
              {actionMessage && (
                <div className="rounded-xl border border-green/20 bg-green-soft px-3.5 py-2.5 text-[14px] text-ink">
                  {actionMessage}
                </div>
              )}
            </div>
            <div className="mt-5 overflow-hidden rounded-2xl border border-line bg-card">
              <OrdiniRitiriSide ritiri={ritiri} prossimo={prossimoRitiro} />
            </div>
          </OrdiniBarraMobile>

          <OrdiniFiltriSheet
            open={filtriAperti}
            onClose={() => setFiltriAperti(false)}
            onReset={handleReset}
            onShowAll={handleShowAll}
            quanti={orders.length}
          >
            <OrdiniFiltriFoglio
              activeChips={activeChips}
              onChipToggle={handleChipToggle}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
              supplierOptions={supplierOptions}
              selectedSuppliers={supplierFilter}
              onSupplierFilterChange={setSupplierFilter}
              channelOptions={channelOptions}
              selectedChannels={channelFilter}
              onChannelFilterChange={setChannelFilter}
              conteggi={conteggiChip}
            />
          </OrdiniFiltriSheet>
        </>
      )}
      {returnDialog}
      {pickupMail.dialog}
      {portalItyhome.dialog}
      {portalItamoby.dialog}
      {portalBizzotto.dialog}
    </main>
  );
}
